/**
 * Core-100 R4 — CORE-BR Responsibility 6: weather context reaches the ASSIGNMENT surface.
 *
 * Renders the `weatherAdvisory` smart-assign attaches to a technician whose bay is designated outdoor
 * (supabase/functions/_shared/weatherAssignmentAdvisory.ts). Information only: there is no control
 * here, nothing is disabled, and the ranking it sits in keeps its place — weather must not block work.
 * Every sentence shown is either the shop's STORED policy explanation, verbatim, or a statement that
 * no alert covers the window / the record could not be read; the reading kind is labelled through the
 * one shared label (`weatherReadingLabel`), so a forecast is never presented as observed.
 */
import { CloudRain, HelpCircle } from 'lucide-react';
import { weatherReadingLabel } from '@/lib/weather/readingKind';

export interface RankingWeatherAdvisoryItem {
  alertId: string;
  reason: string;
  observationKind: 'current_observation' | 'forecast' | null;
  policyThreshold: string | null;
  policyExplanation: string | null;
  weatherEffectiveAt: string | null;
  weatherSource: string | null;
}

export type RankingWeatherAdvisory =
  | { state: 'conflict'; outdoorBays: string[]; items: RankingWeatherAdvisoryItem[]; advisoryOnly: true }
  | { state: 'no_open_alert' | 'unavailable'; outdoorBays: string[]; explanation: string; advisoryOnly: true };

export function RankingWeatherNotice({ advisory, technicianId }: { advisory: RankingWeatherAdvisory | null | undefined; technicianId: string }) {
  if (!advisory) return null;
  const bays = advisory.outdoorBays.join(', ');
  if (advisory.state === 'conflict') {
    return (
      <div
        className="rounded-md border border-amber-500/50 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300 space-y-1"
        data-testid={`ranking-weather-${technicianId}`}
        data-weather-state="conflict"
        role="note"
      >
        <p className="flex items-center gap-1 font-medium">
          <CloudRain className="h-3.5 w-3.5" aria-hidden /> Outdoor bay ({bays}): the shop&apos;s weather rule is crossed at this time
        </p>
        {advisory.items.map((it) => (
          <div key={it.alertId} data-testid={`ranking-weather-item-${it.alertId}`} data-observation-kind={it.observationKind ?? 'unknown'}>
            <span className="font-semibold uppercase tracking-wide">{weatherReadingLabel(it.observationKind)}</span>
            {it.policyExplanation ? <span className="italic"> — {it.policyExplanation}</span> : <span> — {it.reason}</span>}
          </div>
        ))}
        <p className="text-muted-foreground">Advisory only — this technician stays selectable.</p>
      </div>
    );
  }
  return (
    <div
      className="rounded-md border border-dashed border-muted-foreground/40 p-2 text-xs text-muted-foreground flex items-start gap-1"
      data-testid={`ranking-weather-${technicianId}`}
      data-weather-state={advisory.state}
      role="note"
    >
      <HelpCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
      <span>Outdoor bay ({bays}): {advisory.explanation}</span>
    </div>
  );
}
